import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Phone, Trash2, ArrowRight, Search, X as XIcon, Share2, UserCheck, Heart, CalendarRange,
} from 'lucide-react';
import {
  useListCallLogsQuery,
  useGetCallLogStatsQuery,
  useDiscardCallMutation,
  useDeleteCallMutation,
  useRouteCallMutation,
} from '../../api/callLogsApi';
import CallLogDetailDrawer from './CallLogDetailDrawer';
import ConvertCallToLeadModal from './ConvertCallToLeadModal';
import RouteCallModal, { ROUTED_TO_LABELS } from './RouteCallModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import { apiErrorMessage } from '../../utils/apiError';
import { openPatientDetail } from '../ui/detailPanelSlice';
import { toast } from 'react-toastify';
import './callLogs.css';

const POLL_MS = 5000;

const FILTERS = [
  { key: 'new',      label: 'Today' },
  { key: 'lead',     label: 'Existing Lead' },
  { key: 'patient',  label: 'Existing Patient' },
  { key: 'converted',label: 'Converted' },
  { key: 'discarded',label: 'Discarded' },
  { key: 'other',    label: 'Other' },
  { key: 'all',      label: 'All' },
];

function relativeTime(iso) {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return sec <= 5 ? 'just now' : `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min${min > 1 ? 's' : ''} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr${hr > 1 ? 's' : ''} ago`;
  const d = Math.round(hr / 24);
  if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
  return new Date(iso).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
}

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// "Today" evaluated in Indian Standard Time regardless of the browser's local timezone —
// shift now() into IST to read the calendar date, then convert that date's start/end back
// into real UTC instants for the API.
function todayRangeIST() {
  const istNow = new Date(Date.now() + IST_OFFSET_MS);
  const y = istNow.getUTCFullYear();
  const m = istNow.getUTCMonth();
  const d = istNow.getUTCDate();
  const start = new Date(Date.UTC(y, m, d, 0, 0, 0, 0) - IST_OFFSET_MS);
  const end = new Date(Date.UTC(y, m, d, 23, 59, 59, 999) - IST_OFFSET_MS);
  return { from: start.toISOString(), to: end.toISOString() };
}

function filterToQuery(key) {
  switch (key) {
    case 'new':       return { ...todayRangeIST(), };
    case 'lead':      return { isExistingLead: true, isDiscarded: false, isRoutedToOther: false };
    case 'patient':   return { isExistingPatient: true, isDiscarded: false, isRoutedToOther: false };
    case 'converted': return { isConverted: true };
    case 'discarded': return { isDiscarded: true };
    case 'other':     return { isRoutedToOther: true };
    case 'all':
    default:          return {};
  }
}

export default function CallLogsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState('new');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [routeTarget, setRouteTarget] = useState(null);

  // Opt-in custom date range — lets staff look up calls from any past window (not just "Today"),
  // combinable with the status chips above. When on, it takes priority over the "Today" chip's
  // own hardcoded range (see queryArgs spread order below).
  const [showDateRange, setShowDateRange] = useState(false);
  const dr = useDateRange('7d');

  const queryArgs = useMemo(
    () => ({
      page, pageSize: 30, search, ...filterToQuery(filter),
      ...(showDateRange && dr.range.from && dr.range.to ? { from: dr.range.from, to: dr.range.to } : {}),
    }),
    [page, search, filter, showDateRange, dr.range]
  );

  const { data, isFetching, isError, refetch } = useListCallLogsQuery(queryArgs, {
    pollingInterval: POLL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { data: stats } = useGetCallLogStatsQuery(undefined, {
    pollingInterval: POLL_MS,
  });

  const [discardCall, { isLoading: discarding }] = useDiscardCallMutation();
  const [deleteCall, { isLoading: deleting }] = useDeleteCallMutation();
  const [routeCall] = useRouteCallMutation();

  const doDiscard = async () => {
    if (!confirmDiscard) return;
    const c = confirmDiscard;
    setConfirmDiscard(null);
    try {
      await discardCall({ id: c.id, reason: null }).unwrap();
      toast.success('Call discarded');
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not discard')); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const c = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteCall(c.id).unwrap();
      toast.success('Call log deleted');
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not delete')); }
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Call Logs</span></div>
        <div className="page-header-row">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center' }}>
              Call Logs
              <span className="cl-live" title="Auto-refreshes every 5 seconds">
                <span className="cl-live-dot" /> Live
              </span>
            </h1>
            <div className="page-header-sub">Every incoming call captured from the phone app. Grouped by number, with duplicate-detection against leads and patients.</div>
          </div>
        </div>
      </div>

      {stats && (
        <div className="cl-stats">
          <div className="cl-stat-card"><div className="cl-stat-label">Total</div><div className="cl-stat-value">{stats.total}</div></div>
          <div className="cl-stat-card new"><div className="cl-stat-label">New</div><div className="cl-stat-value">{stats.new}</div></div>
          <div className="cl-stat-card today"><div className="cl-stat-label">Today</div><div className="cl-stat-value">{stats.today}</div></div>
          <div className="cl-stat-card lead"><div className="cl-stat-label">Existing Lead</div><div className="cl-stat-value">{stats.existingLeads}</div></div>
          <div className="cl-stat-card patient"><div className="cl-stat-label">Existing Patient</div><div className="cl-stat-value">{stats.existingPatients}</div></div>
          <div className="cl-stat-card conv"><div className="cl-stat-label">Converted</div><div className="cl-stat-value">{stats.converted}</div></div>
          <div className="cl-stat-card discard"><div className="cl-stat-label">Discarded</div><div className="cl-stat-value">{stats.discarded}</div></div>
          <div className="cl-stat-card other"><div className="cl-stat-label">Sent to Other</div><div className="cl-stat-value">{stats.routedToOther ?? 0}</div></div>
        </div>
      )}

      <div className="cl-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`cl-filter-chip ${filter === f.key ? 'active' : ''}`}
            onClick={() => { setFilter(f.key); setPage(1); }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="toolbar" style={{ flexWrap: 'wrap', rowGap: 8 }}>
        <div className="search-field" style={{ flex: 1, maxWidth: 360 }}>
          <Search size={14} className="si" />
          <input
            className="form-control"
            placeholder="Search by phone or name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        {search && (
          <button className="btn btn-sm btn-outline" onClick={() => setSearch('')}><XIcon size={12} /> Clear</button>
        )}
        <button
          className={`btn btn-sm ${showDateRange ? 'btn-primary' : 'btn-outline'}`}
          style={{ gap: 6 }}
          onClick={() => { setShowDateRange((v) => !v); setPage(1); }}
          title="Look up previous calls within a date range"
        >
          <CalendarRange size={13} /> Date Range
        </button>
        {showDateRange && (
          <>
            <DateRangePicker
              size="sm"
              preset={dr.preset} onPresetChange={(p) => { dr.setPreset(p); setPage(1); }}
              customFrom={dr.customFrom} customTo={dr.customTo}
              onCustomChange={(v) => { dr.onCustomChange(v); setPage(1); }}
            />
            <button className="btn btn-sm btn-outline" onClick={() => { setShowDateRange(false); setPage(1); }}>
              <XIcon size={12} /> Clear
            </button>
          </>
        )}
        <div className="toolbar-right">
          <span className="td-muted" style={{ fontSize: '0.78rem' }}>
            {data ? `${data.totalCount} total` : ''}
          </span>
        </div>
      </div>

      {isError && (
        <div className="auth-error" style={{ marginBottom: 14 }}>
          Could not load call logs. <button className="btn btn-sm btn-outline" onClick={refetch}>Retry</button>
        </div>
      )}

      {isFetching && (!data?.items?.length) && (
        <div className="skeleton" style={{ height: 120, marginBottom: 14 }} />
      )}

      <div className="cl-grid">
        {(data?.items ?? []).map((c) => {
          const status = c.isDiscarded ? 'discarded' :
                         c.status === 'Converted' ? 'converted' :
                         c.status === 'Reviewed'  ? 'reviewed' : 'new';
          // "Sent to Other" must not be a dead end — staff can still convert the call to a lead
          // afterwards (canConvert never depends on isRoutedToOther), and can re-route it again
          // if it was sent to the wrong department (canRoute stays available, not locked once used).
          const canConvert = !c.isDiscarded && c.status !== 'Converted' && !c.isExistingPatient && !c.isExistingLead;
          const canRoute   = !c.isDiscarded && c.status !== 'Converted' && !c.isExistingPatient && !c.isExistingLead;
          return (
            <div
              key={c.id}
              className={`cl-card ${c.isDiscarded ? 'discarded' : ''} ${c.status === 'Converted' ? 'converted' : ''}`}
              onClick={() => setOpenId(c.id)}
            >
              <div className={`cl-card-stripe ${status}`} />

              <div className="cl-card-top">
                <div className="cl-card-icon"><Phone size={18} /></div>
                <div className="cl-card-main">
                  <div className="cl-card-phone">{c.phoneNumberRaw || c.phoneNumber}</div>
                  <div className="cl-card-name">{c.callerName || 'Unknown caller'}</div>
                </div>
              </div>

              <div className="cl-card-meta">
                <span className={`cl-card-calls ${c.totalCalls >= 3 ? 'many' : ''}`}>
                  <Phone size={11} /> {c.totalCalls} call{c.totalCalls !== 1 ? 's' : ''}
                </span>
                <span className="cl-card-time" title={new Date(c.lastCallAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}>
                  Last: {relativeTime(c.lastCallAt)}
                </span>
              </div>

              <div className="cl-card-badges">
                {c.status === 'Converted' && <span className="badge b-green">Converted</span>}
                {c.isDiscarded && <span className="badge b-gray">Discarded</span>}
                {c.isRoutedToOther && (
                  <span className="badge b-purple" title={c.routedReason || ''}>
                    Sent to {ROUTED_TO_LABELS[c.routedTo] || 'Other'}
                  </span>
                )}
                {c.isExistingPatient && (
                  <button
                    className="badge b-teal cl-badge-link"
                    title={`View patient ${c.existingPatientCode}`}
                    onClick={(e) => { e.stopPropagation(); dispatch(openPatientDetail(c.existingPatientId)); }}
                  >
                    <Heart size={10} /> {c.existingPatientName || 'Existing Patient'}
                  </button>
                )}
                {c.isExistingLead && !c.isExistingPatient && (
                  <button
                    className="badge b-amber cl-badge-link"
                    title={`View lead — ${c.existingLeadStatus}`}
                    onClick={(e) => { e.stopPropagation(); navigate(`/leads?openId=${c.existingLeadId}`); }}
                  >
                    <UserCheck size={10} /> {c.existingLeadName || 'Existing Lead'}
                  </button>
                )}
                {!c.isExistingLead && !c.isExistingPatient && c.status === 'New' && (
                  <span className="badge b-blue">New caller</span>
                )}
              </div>

              <div className="cl-card-actions" onClick={(e) => e.stopPropagation()}>
                {canConvert && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => setConvertTarget(c)}
                    title="Convert to lead"
                  >
                    <ArrowRight size={12} /> To Lead
                  </button>
                )}
                {canRoute && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setRouteTarget(c)}
                    title={c.isRoutedToOther ? 'Change routing destination' : 'Route to another department'}
                  >
                    <Share2 size={12} /> {c.isRoutedToOther ? 'Re-route' : 'Send to Other'}
                  </button>
                )}
                {!c.isDiscarded && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setConfirmDiscard(c)}
                    title="Discard"
                  >
                    Discard
                  </button>
                )}
                <button
                  className="btn-icon-sm"
                  title="Delete"
                  onClick={() => setConfirmDelete(c)}
                >
                  <Trash2 size={14} color="var(--red-500)" />
                </button>
              </div>
            </div>
          );
        })}

        {!isFetching && data?.items?.length === 0 && (
          <div className="card card-body" style={{ gridColumn: '1 / -1' }}>
            <div className="empty-state">
              <Phone className="es-icon" size={42} />
              <h3>No calls yet</h3>
              <p>
                {filter === 'all'
                  ? 'When the clinic phone app records an incoming call, it will appear here in real-time.'
                  : 'No calls match this filter.'}
              </p>
            </div>
          </div>
        )}
      </div>

      {data && data.totalPages > 1 && (
        <div className="toolbar" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn btn-outline btn-sm" disabled={!data.hasPrevious}
            onClick={() => setPage(page - 1)}>Prev</button>
          <span className="td-muted">Page {data.page} / {data.totalPages}</span>
          <button className="btn btn-outline btn-sm" disabled={!data.hasNext}
            onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}

      {openId && (
        <CallLogDetailDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          onConvert={(cl) => { setOpenId(null); setConvertTarget(cl); }}
        />
      )}

      {convertTarget && (
        <ConvertCallToLeadModal
          callLog={convertTarget}
          onClose={() => setConvertTarget(null)}
        />
      )}

      {routeTarget && (
        <RouteCallModal
          callLog={routeTarget}
          onClose={() => setRouteTarget(null)}
          onSubmit={async ({ routedTo, reason }) => {
            try {
              await routeCall({ id: routeTarget.id, routedTo, reason }).unwrap();
              toast.success(`Sent to ${ROUTED_TO_LABELS[routedTo]}`);
              setRouteTarget(null);
            } catch (e) {
              toast.error(apiErrorMessage(e, 'Could not route call'));
            }
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDiscard)}
        title="Discard this call?"
        message={`The call from ${confirmDiscard?.phoneNumberRaw} will be moved to the Discarded list. If they call again it'll come back as a new call.`}
        confirmLabel={discarding ? 'Discarding…' : 'Discard'}
        variant="danger"
        onConfirm={doDiscard}
        onCancel={() => setConfirmDiscard(null)}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete this call log permanently?"
        message={`This removes ${confirmDelete?.phoneNumberRaw} and all ${confirmDelete?.totalCalls} call(s) from the log. This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}
